import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Eye, EyeOff, KeyRound, Mail, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('admin@kianadevelopers.com');
  const [password, setPassword] = useState('admin123');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const r = await login(email, password);
    setLoading(false);
    if (r.ok) {
      toast.success('Welcome back to Kiana CRM');
      navigate('/dashboard');
    } else {
      toast.error(r.error || 'Login failed');
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel - hidden on mobile, visible on lg+ */}
      <div className="login-bg text-white p-10 lg:p-14 hidden lg:flex flex-col justify-between relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-brand-gold/15 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-white/5 blur-3xl" />

        <div className="flex items-center gap-3 relative">
          <div className="w-11 h-11 rounded-md bg-brand-gold flex items-center justify-center text-[#0F1F38] font-display font-extrabold text-lg">
            K
          </div>
          <div>
            <div className="font-display text-xl tracking-wider font-bold">KIANA DEVELOPERS</div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-brand-gold">Real Estate CRM</div>
          </div>
        </div>

        <div className="relative">
          <div className="text-brand-gold text-xs uppercase tracking-[0.3em] mb-3">20+ Years of Excellence</div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold leading-tight">
            Building Dreams,<br />
            <span className="text-brand-gold">Creating Landmarks.</span>
          </h1>
          <p className="mt-5 text-slate-300 max-w-md">
            The intelligent CRM platform crafted for premium real estate — manage leads,
            schedule site visits, track bookings and grow revenue, all in one place.
          </p>

          <div className="grid grid-cols-3 gap-4 mt-10 max-w-md">
            {[
              { k: '10+', v: 'Projects' },
              { k: '2,000+', v: 'Families' },
              { k: '5+', v: 'Industrial Awards' },
            ].map((s) => (
              <div key={s.v} className="border-l-2 border-brand-gold pl-3">
                <div className="font-display text-2xl font-bold">{s.k}</div>
                <div className="text-xs text-slate-300">{s.v}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-xs text-slate-400 relative">
          © {new Date().getFullYear()} Kiana Developers. All rights reserved.
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-4 sm:p-6 lg:p-12 bg-white min-h-screen">
        <div className="w-full max-w-md">
          {/* Mobile brand header */}
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <div className="w-11 h-11 rounded-md bg-brand-navy flex items-center justify-center text-brand-gold font-display font-extrabold text-lg">K</div>
            <div>
              <div className="font-display text-lg tracking-wider font-bold text-slate-900">KIANA DEVELOPERS</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-brand-gold">Real Estate CRM</div>
            </div>
          </div>
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-brand-gold mb-3">
              <ShieldCheck className="w-4 h-4" /> Secure Sign-in
            </div>
            <h2 className="font-display text-3xl font-bold text-slate-900">Welcome back</h2>
            <p className="text-slate-500 mt-1">Sign in to your Kiana CRM workspace.</p>
          </div>

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="email">Work Email</Label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  className="pl-9 h-11"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@kianadevelopers.com"
                  required
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <button type="button" className="text-xs text-brand-navy hover:text-[#C8A464]">
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <KeyRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="password"
                  type={show ? 'text' : 'password'}
                  className="pl-9 pr-10 h-11"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShow(!show)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                >
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <Checkbox id="remember" defaultChecked /> Remember me for 30 days
              </label>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-brand-navy hover:bg-[#13294A] text-white font-semibold tracking-wide"
            >
              {loading ? 'Signing in...' : 'Sign in to CRM'}
            </Button>

            <div className="flex items-center gap-3 text-xs text-slate-400">
              <div className="h-px bg-slate-200 flex-1" /> Authorized personnel only <div className="h-px bg-slate-200 flex-1" />
            </div>

            <div className="text-xs text-slate-500 text-center">
              Access is restricted to registered Kiana Developers team accounts.<br />
              Contact your administrator if you need access or a password reset.
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
